// angular
import { Injectable } from '@angular/core';

// addons
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, delay, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';

// store
import { select, Store } from '@ngrx/store';
import * as fromStore from '@store/index';
import { State } from '@store/index';
import * as actions from './actions';
import * as feedActions from '../feed/actions';
import * as userActions from '../user/actions';
import * as tradeActions from '../trade/actions';
import * as tradesActions from '../trades/actions';
import * as membersActions from '../members/actions';
import * as tradeSelectors from '../trade/selectors';
import * as organizationActions from '../organization/actions';
import * as organizationSelectors from '../organization/selectors';

// models
import {
  IBonusTradeSubmitWithFile,
  IError,
  IOrganization,
  IOrganizationCreate,
  IOrganizationEdit,
  IOrganizationSubmit,
  IToast,
  ITrade,
  ITradeCreationResult,
  ITradeSubmitWithFile,
  ITransaction,
  IUser,
  OrganizationStatus,
  OrganizationStatusEnum,
  ToastContextEnum,
  TradeImportFailCauseEnum
} from '@shared/models';

// service
import { OrganizationService, TradeService } from '@core/services';

// settings
import { settings } from '@configs';

@Injectable()
export class UiEffects {
  hideToast$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.OpenToast),
    delay(6000),
    map(({ toast: { id }}: { toast: IToast }) => actions.closeToast({ id }))
  ));

  changeFeedFilter$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.ChangeFeedFilter),
    map(_ => feedActions.load())
  ));

  destroyFormAfterSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.SubmitFormSuccess),
    map(({ id }: { id: string }) => actions.destroyForm({ id }))
  ));

  submitInitOrganizationForm$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.SubmitForm),
    filter(({ id }) => id === settings.forms.initiateOrgFormId),
    mergeMap(({ id, formValue }: { id: string; formValue: IOrganizationSubmit }) => {
      return this.organizationService.initiateOrganization(formValue).pipe(
        mergeMap(() => {
          const status = OrganizationStatusEnum.Pending;

          return this.organizationService.setOrganizationStatusToCache(status).pipe(
            concatMap((user: IUser) => {
              return [
                actions.submitFormSuccess({ id }),
                userActions.updateOrganization({ user }),
                actions.openToast({ toast: {
                    id: Math.random(),
                    message: 'Thanks for filling in the form. We will contact you for the next step (formalize the Stak) within a few working days.',
                    context: ToastContextEnum.Success
                  }
                })
              ];
            })
          );
        }),
        catchError((error: IError) => of(
          actions.submitFormFail({ id, error }),
          actions.openToast({ toast: {
            id: Math.random(),
            message: error.message,
            context: ToastContextEnum.Error
          }})
        ))
      );
    })
  ));

  submitInitOrganizationDraftForm$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.SubmitForm),
    filter(({ id }) => id === settings.forms.initiateOrgDraftFormId),
    switchMap(({ id, formValue }: { id: string; formValue: IOrganizationSubmit }) => {
      return this.organizationService.saveDraftInitiationOrganization(formValue).pipe(
        concatMap(() => {
          return [];
        }),
        catchError((error: IError) => of(
          actions.submitFormFail({ id, error }),
          actions.openToast({ toast: {
            id: Math.random(),
            message: error.message,
            context: ToastContextEnum.Error
          }})
        ))
      );
    })
  ));

  submitUserBlocksBalanceForm$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.SubmitForm),
    filter(({ id }) => id === settings.forms.userBlocksBalanceFormId),
    withLatestFrom(this.store.pipe(select(tradeSelectors.getTrade))),
    mergeMap(([{ id, formValue: userBlocksBalance }, trade]: [{ id: string; formValue: number }, ITrade ]) => {
      return this.tradeService.updateBlocksBalance(trade.id, userBlocksBalance).pipe(
        concatMap((transaction: ITransaction) => {
          return [
            actions.submitFormSuccess({ id }),
            tradeActions.updateBlockBalance({ transaction }),
            feedActions.load()
          ];
        }),
        catchError((error: IError) => of(
          actions.submitFormFail({ id, error }),
          actions.openToast({ toast: {
            id: Math.random(),
            message: error.message,
            context: ToastContextEnum.Error
          }})
        ))
      );
    })
  ));

  submitBonusCertificatesBalanceForm$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.SubmitForm),
    filter(({ id }) => id === settings.forms.bonusCertificatesBalanceFormId),
    withLatestFrom(this.store.pipe(select(tradeSelectors.getTrade))),
    mergeMap(([{ id, formValue: { userId, certificates} }, trade]: [{ id: string; formValue: {userId: number, certificates: number} }, ITrade ]) => {
      return this.tradeService.updateBonusBlocksBalance(trade.id, userId, certificates).pipe(
        concatMap((transaction: ITransaction) => {
          return [
            actions.submitFormSuccess({ id }),
            tradeActions.updateBlockBalance({ transaction }),
            membersActions.load(),
            tradeActions.load({ id: trade.id }),
            feedActions.load()
          ];
        }),
        catchError((error: IError) => of(
          actions.submitFormFail({ id, error }),
          actions.openToast({ toast: {
            id: Math.random(),
            message: error.message,
            context: ToastContextEnum.Error
          }})
        ))
      );
    })
  ));

  submitInitTradeForm$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.SubmitForm),
    filter(({ id }) => id === settings.forms.initiateTradeFormId),
    mergeMap(({ id, formValue }: { id: string; formValue: ITradeSubmitWithFile}) => {
      return this.tradeService.addTradeWithImport(formValue).pipe(
        concatMap((value: ITradeCreationResult) => {
          let toastAction = null;
          if (!!value) {
            if (!value.success && value.failCause) {
              let message: string = null;
              switch (value.failCause) {
                case TradeImportFailCauseEnum.WrongFileType:
                  message = 'The file you try to import is of a wrong type.';
                  break;
                case TradeImportFailCauseEnum.WrongColumnNames:
                case TradeImportFailCauseEnum.WrongData:
                  message = 'The excel format is wrong. The trade cannot being activated. Please use the right xls format and start the trade again.';
                  break;
                case TradeImportFailCauseEnum.TradeNotFound:
                  message = 'Trade has not been found.';
                  break;
              }
              if (message !== null) {
                toastAction = actions.openToast({ toast: {
                    id: Math.random(),
                    message: message,
                    context: ToastContextEnum.Error
                  }}
                );
              }
            } else if (value.skippedCount > 0) {
              toastAction = actions.openToast({ toast: {
                  id: Math.random(),
                  message: `${value.skippedCount} trade candidates were skipped because they are members of another organization.`,
                  context: ToastContextEnum.Success
                }}
              );
            }
          }
          return [
            actions.submitFormSuccess({id}),
            actions.closeModal({id: settings.modals.initiateTradeModalId}),
            toastAction,
            membersActions.load(),
            !!value.trade ? tradesActions.add({trade: value.trade}) : null
          ].filter(v => v !== null);
        }),
        catchError((error: IError) => of(
          actions.submitFormFail({ id, error }),
          actions.openToast({ toast: {
            id: Math.random(),
            message: error.message,
            context: ToastContextEnum.Error
          }})
        ))
      );
    })
  ));

  submitInitBonusTradeForm$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.SubmitForm),
    filter(({ id }) => id === settings.forms.initiateBonusTradeFormId),
    mergeMap(({ id, formValue }: { id: string; formValue: IBonusTradeSubmitWithFile }) => {
      return this.tradeService.addBonusTradeWithImport(formValue).pipe(
        concatMap((value: ITradeCreationResult) => {
          let toastAction = null;
          if (!!value) {
            if (!value.success && value.failCause) {
              let message: string = null;
              switch (value.failCause) {
                case TradeImportFailCauseEnum.WrongFileType:
                  message = 'The file you try to import is of a wrong type.';
                  break;
                case TradeImportFailCauseEnum.WrongColumnNames:
                case TradeImportFailCauseEnum.WrongData:
                  message = 'The excel format is wrong. The trade cannot being activated. Please use the right xls format and start the trade again.';
                  break;
                case TradeImportFailCauseEnum.TradeNotFound:
                  message = 'Trade has not been found.';
                  break;
              }
              if (message !== null) {
                toastAction = actions.openToast({ toast: {
                    id: Math.random(),
                    message: message,
                    context: ToastContextEnum.Error
                  }}
                );
              }
            } else if (value.skippedCount > 0) {
              toastAction = actions.openToast({ toast: {
                  id: Math.random(),
                  message: `${value.skippedCount} trade candidates were skipped because they are members of another organization.`,
                  context: ToastContextEnum.Success
                }}
              );
            }
          }
          return [
            actions.submitFormSuccess({id}),
            actions.closeModal({id: settings.modals.initiateBonusTradeModalId}),
            toastAction,
            membersActions.load(),
            !!value.trade ? tradesActions.add({trade: value.trade}) : null
          ].filter(v => v !== null);
        }),
        catchError((error: IError) => of(
          actions.submitFormFail({ id, error }),
          actions.openToast({ toast: {
            id: Math.random(),
            message: error.message,
            context: ToastContextEnum.Error
          }})
        ))
      );
    })
  ));

  submitDeclineOrgForm$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.SubmitForm),
    filter(({ id }) => id === settings.forms.declineOrgFormId),
    withLatestFrom(this.store.pipe(select(organizationSelectors.getOrganization))),
    mergeMap(([{ id, formValue: comment }, organization]: [{ id: string; formValue: { comment: string }}, IOrganization ]) => {
      return this.organizationService.declineOrganization(organization.id, comment).pipe(
        concatMap(() => {
          return [
            actions.submitFormSuccess({ id }),
            actions.closeModal({ id: settings.modals.declineOrgModalId }),
            organizationActions.decline({ id: organization.id })
          ];
        }),
        catchError((error: IError) => of(
          actions.submitFormFail({ id, error }),
          actions.openToast({ toast: {
            id: Math.random(),
            message: error.message,
            context: ToastContextEnum.Error
          }})
        ))
      );
    })
  ));

  submitCreateInvestorForm$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.SubmitForm),
    filter(({ id }) => id === settings.forms.createInvestorFormId),
    mergeMap(({ id, formValue }: { id: string; formValue: IOrganizationCreate }) => {
      return this.organizationService.createOrganization(formValue).pipe(
        mergeMap(() => {
          this.store.dispatch(fromStore.OrganizationsActions.load({ status: null }));
          return [
            actions.submitFormSuccess({id}),
            actions.closeModal({id: settings.modals.createInvestorModalId})
          ];
        }),
        catchError((error: IError) => of(
          actions.submitFormFail({ id, error }),
          actions.openToast({ toast: {
              id: Math.random(),
              message: error.message,
              context: ToastContextEnum.Error
            }})
        ))
      );
    })
  ));

  submitEditOrganizationForm$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.SubmitForm),
    filter(({ id }) => id === settings.forms.editOrganizationFormId),
    mergeMap(({ id, formValue, status }: { id: string; formValue: IOrganizationEdit; status?: OrganizationStatus }) => {
      return this.organizationService.updateOrganization(formValue).pipe(
        mergeMap(() => {
          // todo - reload organization or organizations list - ?
          this.store.dispatch(fromStore.OrganizationsActions.load({ status: status }));
          this.store.dispatch(fromStore.OrganizationActions.load({ id: formValue.id }));
          return [
            actions.submitFormSuccess({id}),
            actions.closeModal({id: settings.modals.editOrganizationModalId})
          ];
        }),
        catchError((error: IError) => of(
          actions.submitFormFail({ id, error }),
          actions.openToast({ toast: {
              id: Math.random(),
              message: error.message,
              context: ToastContextEnum.Error
            }})
        ))
      );
    })
  ));

  constructor(
    private actions$: Actions,
    private tradeService: TradeService,
    private organizationService: OrganizationService,
    private store: Store<State>
  ) {}
}
